import { useTranslation } from "react-i18next";
import { PositionBlock } from "@/components/admin-page-components/admin-create-components/invoice-create-components/PositionBlock";
import { v4 as uuidv4 } from 'uuid';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { INVOICE_TYPES } from "@/constants";

const PositionsViewTable = ({ data }) => {
  const { t } = useTranslation();

  const columns = [
    {
      accessorKey: "name",
      header: t("invoices:positions_name"),
      cell: ({ row }) => <div>{row.original.name}</div>,
      key: uuidv4()
    },
    {
      accessorKey: "GTU",
      header: 'GTU',
      cell: ({ row }) => <div>{row.original.GTU}</div>,
      key: uuidv4()
    },
    {
      accessorKey: "pkwiu",
      header: 'PKWIU',
      cell: ({ row }) => <div>{row.original.pkwiu}</div>,
      key: uuidv4()
    },
    {
      accessorKey: "amount",
      header: t("invoices:positions_amount"),
      cell: ({ row }) => <div>{row.original.amount}</div>,
      key: uuidv4()
    },
    {
      accessorKey: "unit",
      header: t("invoices:positions_unit"),
      cell: ({ row }) => (
        <div className='w-[56px] bg-pirpleLightBg rounded text-blueText text-center p-0'>
          {row.original.unit}
        </div>
      ),
      key: uuidv4()
    },
    {
      accessorKey: "priceNetto",
      header: t("invoices:positions_price_netto"),
      cell: ({ row }) => <div>{parseFloat(row.original.priceNetto).toFixed(2)}</div>,
      key: uuidv4()
    },
    {
      accessorKey: "summaryNetto",
      header: t("invoices:positions_summary_netto"),
      cell: ({ row }) => <div>{(row.original.priceNetto * row.original.amount).toFixed(2)}</div>,
      key: uuidv4()
    },
    {
      accessorKey: "typeOfVat",
      header: t("invoices:positions_type_of_vat"),
      cell: ({ row }) => <div>{row.original.typeOfVat}</div>,
      key: uuidv4()
    },
    {
      accessorKey: "vat",
      header: t("invoices:positions_vat"),
      cell: ({ row }) => <div>{(row.original.amount * row.original.vat).toFixed(2)}</div>,
      key: uuidv4()
    },
    {
      accessorKey: "priceBrutto",
      header: t("invoices:positions_price_brutto"),
      cell: ({ row }) => (
        <div>
          {((row.original.priceNetto * row.original.amount) + (row.original.amount * row.original.vat)).toFixed(2)}
        </div>
      ),
      key: uuidv4()
    },
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="rounded-md border p-2">
      {data && (
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={uuidv4()}>
                {headerGroup.headers.map(header => (
                  <TableHead className="pt-3 pb-3 bg-white" key={uuidv4()}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  className="bg-white"
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell className="relative text-sm pt-3 pb-3" key={uuidv4()}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {t('no_results')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export const PositionsSection = ({ form, inputChecked = false }) => {
  const { t } = useTranslation();
  const empty_error = t('empty_data_invalid');
  const format_error = t('format_data_invalid');

  const {
    isViewMode,
    rows,
    setRows,
    invoiceType,
    invoice,
  } = form;

  // View mode
  if (isViewMode) {
    return (
      <div className='w-full'>
        <div className='text-xl font-medium pb-6'>
          {t("invoices:positions_title")}
        </div>
        <div className='pt-5'>
          <PositionsViewTable data={invoice?.invoiceItems ?? []} />
        </div>
      </div>
    );
  }

  // Create/Edit mode
  const hasRowErrors = inputChecked && (
    rows.length === 0 ||
    rows.some(row =>
      !row.name ||
      !row.quantity ||
      (invoiceType !== INVOICE_TYPES.FAKTURA_KONCOWA && !row.unitPriceNetto) ||
      !row.vatRate ||
      !row.unit
    )
  );

  return (
    <div className="w-full">
      <PositionBlock
        inputChecked={inputChecked}
        rows={rows}
        onRowsUpdate={setRows}
        error={hasRowErrors}
        errorMessage={rows.length === 0 ? empty_error : format_error}
      />
    </div>
  );
};
